import React, { useCallback, useEffect, useState } from 'react';
import { Search, Ban, RotateCcw } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { t, formatDateTime } from '../../i18n';
import { Card, Button, Input, Field, Alert, Spinner, Badge } from '../../components/ui';
import { inRange } from '../../lib/settingsForm';
import SettingsCard, { useSettingsCard, NumberWithUnit } from './SettingsCard';

/**
 * What applies to the whole practice, for admin (migration 0060).
 *
 * Only what something reads. The reminder time is the one professionals fall
 * back on when they leave theirs empty in Avisos; the check-in window is when a
 * sede's QR accepts an arrival. Timezone, currency and locale are shown and not
 * editable: a new zone would move every working hour against sessions already
 * booked, and a new currency converts no amount. The practice name, the
 * notification channel and the automatic block for unpaid subscriptions are
 * stored, but nothing reads them, so they are not here.
 *
 * Below them, admin's block on a whole patient account (0062).
 */

// The database refuses values outside these too (0032, 0060).
const LIMITS = {
  reminder_hours_before: [1, 168],
  checkin_opens_minutes_before: [0, 720],
  checkin_closes_minutes_after: [0, 240],
};

const Fixed = ({ label, value }) => (
  <div>
    <dt className="text-xs text-slate-500">{label}</dt>
    <dd className="mt-0.5 font-medium text-slate-900">{value}</dd>
  </div>
);

const savePractice = async (patch, onSaved) => {
  const { data, error } = await supabase.from('practice_settings')
    .update(patch)
    .eq('id', true)
    .select('*')
    .single();
  if (error) return error.message;
  onSaved(data);
  return null;
};

const ReminderCard = ({ row, ownReminders, onSaved }) => {
  const { form, change, save, card } = useSettingsCard('practice-reminder', {
    reminder_hours_before: row.reminder_hours_before,
  });

  const onSave = () => save(async (typed) => {
    if (!inRange(typed.reminder_hours_before, LIMITS.reminder_hours_before)) return t('settings.reminderHoursInvalid');
    return savePractice({ reminder_hours_before: Number(typed.reminder_hours_before) }, onSaved);
  });

  return (
    <SettingsCard title={t('settings.remindersTitle')} subtitle={t('settings.remindersHint')} card={card} onSave={onSave}>
      <Field
        label={t('settings.reminderHours')}
        hint={ownReminders ? t('settings.ownReminders', { count: ownReminders }) : undefined}
      >
        <NumberWithUnit
          min={LIMITS.reminder_hours_before[0]} max={LIMITS.reminder_hours_before[1]}
          unit={t('settings.hoursBefore')}
          value={form.reminder_hours_before}
          onChange={(event) => change({ reminder_hours_before: event.target.value })}
        />
      </Field>
    </SettingsCard>
  );
};

const CheckinCard = ({ row, onSaved }) => {
  const { form, change, save, card } = useSettingsCard('practice-checkin', {
    checkin_opens_minutes_before: row.checkin_opens_minutes_before,
    checkin_closes_minutes_after: row.checkin_closes_minutes_after,
  });

  const onSave = () => save(async (typed) => {
    if (!inRange(typed.checkin_opens_minutes_before, LIMITS.checkin_opens_minutes_before)
      || !inRange(typed.checkin_closes_minutes_after, LIMITS.checkin_closes_minutes_after)) {
      return t('settings.checkinInvalid');
    }
    return savePractice({
      checkin_opens_minutes_before: Number(typed.checkin_opens_minutes_before),
      checkin_closes_minutes_after: Number(typed.checkin_closes_minutes_after),
    }, onSaved);
  });

  return (
    <SettingsCard title={t('settings.checkinTitle')} subtitle={t('settings.checkinHint')} card={card} onSave={onSave}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('settings.checkinOpens')}>
          <NumberWithUnit
            min={LIMITS.checkin_opens_minutes_before[0]} max={LIMITS.checkin_opens_minutes_before[1]}
            unit={t('settings.minutesBefore')}
            value={form.checkin_opens_minutes_before}
            onChange={(event) => change({ checkin_opens_minutes_before: event.target.value })}
          />
        </Field>
        <Field label={t('settings.checkinCloses')}>
          <NumberWithUnit
            min={LIMITS.checkin_closes_minutes_after[0]} max={LIMITS.checkin_closes_minutes_after[1]}
            unit={t('settings.minutesAfter')}
            value={form.checkin_closes_minutes_after}
            onChange={(event) => change({ checkin_closes_minutes_after: event.target.value })}
          />
        </Field>
      </div>
    </SettingsCard>
  );
};

/**
 * Blocking a whole patient account, which stops that person signing in with
 * any professional (0062). Admin finds it by email and sees only the name,
 * the address, whether anyone signs into it, and the block itself: never its
 * patients or sessions, which are their professionals' (0038).
 */
const AccountBlock = () => {
  const [email, setEmail] = useState('');
  // undefined: not searched yet; null: no account with that email.
  const [account, setAccount] = useState(undefined);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const find = async () => {
    if (!email.trim()) return;
    setError(null);
    setBusy(true);
    const { data, error: findError } = await supabase.rpc('admin_find_account', { p_email: email.trim() });
    setBusy(false);
    if (findError) { setError(findError.message); return; }
    setAccount(data?.[0] ?? null);
    setReason('');
  };

  const setBlocked = async (blocked) => {
    const name = `${account.first_name ?? ''} ${account.last_name ?? ''}`.trim() || account.email;
    if (blocked && !window.confirm(t('settings.confirmBlockAccount', { name }))) return;
    setError(null);
    setBusy(true);
    const { data, error: blockError } = await supabase.rpc('admin_set_account_block', {
      p_account_id: account.id, p_blocked: blocked, p_reason: blocked ? reason.trim() : null,
    });
    setBusy(false);
    if (blockError) { setError(blockError.message); return; }
    setAccount({ ...account, ...data });
    setReason('');
  };

  return (
    <Card title={t('settings.accountBlockTitle')} subtitle={t('settings.accountBlockHint')}>
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-0 flex-1">
            <Field label={t('auth.email')}>
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') find(); }}
              />
            </Field>
          </div>
          <Button variant="secondary" onClick={find} loading={busy} disabled={!email.trim()}>
            <Search size={16} />{t('common.search')}
          </Button>
        </div>

        {account === null && <p className="text-sm text-slate-500">{t('settings.accountNotFound')}</p>}

        {account && (
          <div className="space-y-3 rounded-lg border border-slate-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-slate-900">{account.first_name} {account.last_name}</p>
                <p className="break-all text-xs text-slate-500">
                  {account.email} · {t(account.signs_in ? 'settings.accountSignsIn' : 'settings.accountNeverSignedIn')}
                </p>
              </div>
              <Badge tone={account.is_blocked ? 'danger' : 'success'}>
                {t(account.is_blocked ? 'settings.accountBlocked' : 'settings.accountActive')}
              </Badge>
            </div>

            {account.is_blocked ? (
              <>
                <p className="text-sm text-slate-600">
                  {t('settings.accountBlockedSince', {
                    when: formatDateTime(account.blocked_at),
                    reason: account.blocked_reason ?? '—',
                  })}
                </p>
                <Button variant="secondary" onClick={() => setBlocked(false)} loading={busy}>
                  <RotateCcw size={16} />{t('settings.unblockAccount')}
                </Button>
              </>
            ) : (
              <>
                <Field label={t('settings.blockReason')} hint={t('settings.blockReasonHint')}>
                  <Input value={reason} onChange={(event) => setReason(event.target.value)} />
                </Field>
                <Button variant="danger" onClick={() => setBlocked(true)} loading={busy} disabled={!reason.trim()}>
                  <Ban size={16} />{t('settings.blockAccount')}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

const PracticeTab = () => {
  const [row, setRow] = useState(null);
  // How many professionals set their own reminder time, so the default's reach is clear.
  const [ownReminders, setOwnReminders] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const [settingsResult, ownResult] = await Promise.all([
      supabase.from('practice_settings').select('*').eq('id', true).single(),
      supabase.from('therapists').select('id', { count: 'exact', head: true })
        .not('reminder_hours_before', 'is', null),
    ]);
    if (settingsResult.error) { setError(settingsResult.error.message); return; }
    setRow(settingsResult.data);
    setOwnReminders(ownResult.error ? null : (ownResult.count ?? 0));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!row) return error ? <Alert tone="danger" title={t('settings.loadFailed')}>{error}</Alert> : <Spinner />;

  return (
    <div className="space-y-4">
      <Card title={t('settings.clockTitle')} subtitle={t('settings.clockFixed')}>
        <dl className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-[2fr_1fr_1fr]">
          <Fixed label={t('settings.timezone')} value={row.default_timezone} />
          <Fixed label={t('settings.currency')} value={row.currency_code} />
          <Fixed label={t('settings.locale')} value={row.locale} />
        </dl>
      </Card>

      {/* Each saves only its own fields, so the row they return replaces the
          one here without touching what is typed in the other card. */}
      <ReminderCard row={row} ownReminders={ownReminders} onSaved={setRow} />
      <CheckinCard row={row} onSaved={setRow} />
      <p className="text-xs text-slate-500">{t('settings.lastChanged', { when: formatDateTime(row.updated_at) })}</p>

      <AccountBlock />
    </div>
  );
};

export default PracticeTab;
